import { Link, useParams } from 'react-router-dom';
import { useState, useEffect, useRef, Fragment } from 'react';
import { useLeague } from '../../features/leagues/api';
import {
  useMatchday,
  useMatchdayScores,
  useMatchdayStatus,
  useMatchdayPredictions,
  useLiveStandings,
  useSetResult,
  useTransitionMatchday,
  type MatchInfo,
} from '../../features/matchdays/api';
import { useAnnouncer } from '../../components/LiveAnnouncer';
import { usePageTitle } from '../../lib/usePageTitle';
import { formatDateOnly, formatDateTimeFull, formatKickoff, formatPoints, isTimeTBD } from '../../lib/dates';
import { AppLayout } from '../../components/AppLayout';
import { Button } from '../../components/Button';
import { Crest } from '../../components/Crest';
import { LeagueNav } from './LeagueNav';
import { STATE_LABELS } from './MatchdayListPage';
import { api, ApiError } from '../../lib/api';
import { useQueryClient } from '@tanstack/react-query';

/** Prossima transizione sensata per ogni stato (flusso guidato per l'admin). */
const NEXT_TRANSITIONS: Record<
  string,
  { to: string; label: string; confirm?: string; secondary?: boolean }[]
> = {
  DRAFT: [{ to: 'SCHEDULED', label: 'Programma la giornata' }],
  SCHEDULED: [{ to: 'OPEN', label: 'Apri i pronostici' }],
  OPEN: [
    {
      to: 'CLOSED',
      label: 'Chiudi i pronostici ORA',
      confirm:
        'Vuoi davvero chiudere i pronostici ADESSO, prima della scadenza? Chi non ha giocato prenderà la colonna rossa. (Se hai le automazioni attive, la chiusura avviene comunque da sola alla scadenza.)',
    },
  ],
  CLOSED: [
    { to: 'RESULTS_PENDING', label: 'Passa ai risultati' },
    {
      to: 'OPEN',
      label: 'Riapri i pronostici',
      secondary: true,
      confirm:
        'Riaprire i pronostici? Le colonne rosse e la schedina del bot verranno annullate, le schedine dei giocatori sbloccate, e tutti riceveranno una notifica.',
    },
  ],
  RESULTS_PENDING: [
    { to: 'RESULTS_VERIFIED', label: 'Conferma i risultati' },
    {
      to: 'OPEN',
      label: 'Riapri i pronostici',
      secondary: true,
      confirm:
        'Riaprire i pronostici? Le colonne rosse e la schedina del bot verranno annullate, le schedine dei giocatori sbloccate, e tutti riceveranno una notifica.',
    },
  ],
  RESULTS_VERIFIED: [{ to: 'SCORING', label: 'Calcola i punteggi' }],
  SCORING: [{ to: 'PUBLISHED', label: 'Pubblica la classifica' }],
  PUBLISHED: [{ to: 'SCORING', label: 'Ricalcola i punteggi' }],
};

export function MatchdayDetailPage() {
  const { id = '', mdId = '' } = useParams();
  const league = useLeague(id);
  const query = useMatchday(id, mdId);
  const matchday = query.data?.matchday;
  usePageTitle(matchday ? `Giornata ${matchday.number}` : 'Giornata');

  const isAdmin = league.data?.myRole === 'ADMIN' || league.data?.myRole === 'SUPER_ADMIN';
  const showScores = matchday?.state === 'PUBLISHED' || (isAdmin && matchday?.state === 'SCORING');

  if (query.isLoading) {
    return (
      <AppLayout title="Giornata">
        <p role="status" className="mt-4">Caricamento…</p>
      </AppLayout>
    );
  }
  if (!matchday) {
    return (
      <AppLayout title="Giornata non trovata">
        <p role="alert" className="mt-4 font-medium text-danger">Questa giornata non esiste.</p>
      </AppLayout>
    );
  }

  const myPrediction = query.data?.myPrediction ?? null;

  return (
    <AppLayout title={`Giornata ${matchday.number}${matchday.isSuper ? ' ⭐' : ''}`}>
      <LeagueNav leagueId={id} />

      <p className="mt-4 text-lg">
        Stato: <strong>{STATE_LABELS[matchday.state] ?? matchday.state}</strong>
        {matchday.state === 'OPEN' && matchday.deadline && (
          <> — chiude {formatDateTimeFull(matchday.deadline)}</>
        )}
      </p>
      {matchday.state === 'SCHEDULED' && league.data?.automation.autoDeadline && (
        <p className="mt-1 text-ink-700 dark:text-ink-200">
          I pronostici si apriranno <strong>automaticamente entro pochi minuti</strong> e si
          chiuderanno da soli {league.data.automation.autoDeadlineMinutesBefore} minuti prima della
          prima partita. Non devi fare nulla{isAdmin ? ' (ma da admin puoi aprirli subito con il pulsante qui sotto)' : ''}.
        </p>
      )}

      {/* CTA principale per il giocatore */}
      {matchday.state === 'OPEN' && (
        <div className="mt-4">
          <Link
            to={`/leghe/${id}/giornate/${mdId}/pronostici`}
            className="button inline-flex items-center justify-center rounded-xl bg-panda-700 px-8 py-3 text-lg font-semibold text-white no-underline hover:bg-panda-800 dark:bg-panda-600"
          >
            {myPrediction ? 'Modifica i pronostici' : 'Fai i pronostici'}
          </Link>
          {myPrediction && (
            <p role="status" className="mt-2 text-ink-700 dark:text-ink-200">
              Schedina inviata: puoi modificarla fino alla chiusura.
            </p>
          )}
        </div>
      )}

      {myPrediction?.isRedColumn && matchday.state !== 'OPEN' && (
        <p role="status" className="mt-3 rounded-lg border-2 border-warning bg-amber-50 p-3 font-medium text-warning dark:bg-ink-800">
          Per questa giornata ti è stata assegnata la colonna rossa: schedina precedente ripetuta
          con penalità, come da regolamento.
        </p>
      )}

      {/* Fase live: la Diretta sostituisce la tabella partite (che resta
          agli admin per l'inserimento risultati) */}
      {(() => {
        const inLive = ['CLOSED', 'RESULTS_PENDING', 'RESULTS_VERIFIED', 'SCORING'].includes(
          matchday.state,
        );
        const overLevels = query.data?.rules.overLevels ?? [];
        const overMinGoals =
          query.data?.rules.overEnabled && overLevels.length > 0
            ? Math.min(...overLevels.map((l) => l.goals))
            : null;
        return (
          <>
            {inLive && (
              <LiveBoardSection
                matches={matchday.matches}
                leagueId={id}
                matchdayId={mdId}
                overMinGoals={overMinGoals}
              />
            )}

            {inLive && <LiveStandingsSection leagueId={id} matchdayId={mdId} />}

            {(!inLive || isAdmin) && (
              <section aria-labelledby="sez-partite" className="mt-8">
                <h2 id="sez-partite" className="text-2xl font-bold tracking-tight">Partite</h2>
                <MatchTable
                  matches={matchday.matches}
                  leagueId={id}
                  matchdayId={mdId}
                  canEditResults={
                    isAdmin &&
                    ['CLOSED', 'RESULTS_PENDING', 'RESULTS_VERIFIED'].includes(matchday.state)
                  }
                />
              </section>
            )}
          </>
        );
      })()}

      {/* Punteggi pubblicati */}
      {showScores && <ScoresSection leagueId={id} matchdayId={mdId} isAdmin={isAdmin} />}

      {/* Le schedine di tutti (visibili solo a pronostici chiusi) */}
      {!['DRAFT', 'SCHEDULED', 'OPEN'].includes(matchday.state) && (
        <RevealedPredictionsSection leagueId={id} matchdayId={mdId} matches={matchday.matches} />
      )}

      {/* Contestazione (giocatori, a giornata pubblicata) */}
      {matchday.state === 'PUBLISHED' && !isAdmin && (
        <DisputeSection leagueId={id} matchdayId={mdId} />
      )}

      {/* Pannello admin */}
      {isAdmin && <AdminPanel leagueId={id} matchdayId={mdId} state={matchday.state} />}
    </AppLayout>
  );
}

/** Quali esiti copre ogni segno (per colorare i pronostici in diretta). */
const PICK_COVERS: Record<string, string[]> = {
  HOME: ['HOME'],
  DRAW: ['DRAW'],
  AWAY: ['AWAY'],
  HOME_DRAW: ['HOME', 'DRAW'],
  DRAW_AWAY: ['DRAW', 'AWAY'],
  HOME_AWAY: ['HOME', 'AWAY'],
};

type ChipTone = 'winning' | 'losing' | 'pending';

const CHIP_CLASSES: Record<ChipTone, string> = {
  winning:
    'border-success bg-panda-50 text-success dark:bg-ink-900 dark:text-[#5dd879] dark:border-[#5dd879]',
  losing: 'border-danger bg-red-50 text-danger dark:bg-ink-900 dark:text-[#ff8b94] dark:border-[#ff8b94]',
  pending: 'border-ink-300 text-ink-600 dark:border-ink-600 dark:text-ink-300',
};

/**
 * LA DIRETTA — vista mobile-first per seguire la giornata in tempo reale:
 * una carta per partita col punteggio grande e, dentro, i pronostici di
 * TUTTI i giocatori colorati secondo l'andamento (✓ vincente, ✗ perdente,
 * in attesa se non è ancora iniziata).
 *
 * Accessibilità: ogni carta è un articolo con heading; ogni pronostico ha
 * il testo completo per screen reader; i GOL vengono annunciati nella live
 * region (solo ai cambi di punteggio, mai a ogni aggiornamento).
 */
function LiveBoardSection({
  matches,
  leagueId,
  matchdayId,
  overMinGoals,
}: {
  matches: MatchInfo[];
  leagueId: string;
  matchdayId: string;
  overMinGoals: number | null;
}) {
  const predictions = useMatchdayPredictions(leagueId, matchdayId, true);
  const { announce } = useAnnouncer();
  const prevScores = useRef<Map<string, string> | null>(null);

  // Annuncio gol/fine partita: solo sulle VARIAZIONI di punteggio o stato.
  useEffect(() => {
    const current = new Map(
      matches.map((m) => [m.id, `${m.status}:${m.homeScore ?? ''}-${m.awayScore ?? ''}`]),
    );
    if (prevScores.current) {
      for (const m of matches) {
        const prev = prevScores.current.get(m.id);
        const now = current.get(m.id);
        if (prev && now && prev !== now && m.homeScore != null && m.awayScore != null) {
          const name = `${m.homeTeam.shortName} ${m.awayTeam.shortName}`;
          if (m.status === 'FINISHED' && !prev.startsWith('FINISHED')) {
            announce(`Finita: ${name} ${m.homeScore} a ${m.awayScore}.`);
          } else {
            announce(`Gol! ${name}: ora ${m.homeScore} a ${m.awayScore}.`);
          }
        }
      }
    }
    prevScores.current = current;
  }, [matches, announce]);

  if (!predictions.data) return null;
  const players = predictions.data;

  const chipFor = (
    match: MatchInfo,
    kind: 'pick' | 'exact' | 'over',
    player: (typeof players)[number],
  ): { tone: ChipTone; visible: string; sr: string } | null => {
    const hasScore = match.homeScore != null && match.awayScore != null;
    const started = hasScore && (match.status === 'LIVE' || match.status === 'FINISHED');
    const finished = match.status === 'FINISHED';
    const outcome = !started
      ? null
      : match.homeScore! > match.awayScore!
        ? 'HOME'
        : match.homeScore! < match.awayScore!
          ? 'AWAY'
          : 'DRAW';

    if (kind === 'pick') {
      const pick = player.items.find((i) => i.matchId === match.id)?.pick;
      if (!pick) return null;
      const sign = PICK_SHORT[pick] ?? pick;
      if (!started)
        return { tone: 'pending', visible: `${player.displayName} ${sign}`, sr: `${player.displayName} ha giocato ${sign}: partita non ancora iniziata` };
      const winning = PICK_COVERS[pick]?.includes(outcome!) ?? false;
      return {
        tone: winning ? 'winning' : 'losing',
        visible: `${player.displayName} ${sign}`,
        sr: `${player.displayName} ha giocato ${sign}: ${finished ? (winning ? 'pronostico vinto' : 'pronostico perso') : winning ? 'per ora vincente' : 'per ora perdente'}`,
      };
    }

    if (kind === 'exact') {
      if (player.exact?.matchId !== match.id) return null;
      const guess = `${player.exact.home}-${player.exact.away}`;
      if (!started)
        return { tone: 'pending', visible: `${player.displayName} 🎯 ${guess}`, sr: `${player.displayName} ha pronosticato il risultato esatto ${guess}: partita non ancora iniziata` };
      const hit = match.homeScore === player.exact.home && match.awayScore === player.exact.away;
      return {
        tone: hit ? 'winning' : 'losing',
        visible: `${player.displayName} 🎯 ${guess}`,
        sr: `${player.displayName} risultato esatto ${guess}: ${hit ? (finished ? 'indovinato!' : 'al momento esatto') : finished ? 'non indovinato' : 'al momento non corrisponde'}`,
      };
    }

    // over
    if (player.overMatchId !== match.id) return null;
    if (!started || overMinGoals === null)
      return { tone: 'pending', visible: `${player.displayName} 🔥`, sr: `${player.displayName} ha giocato l'OVER su questa partita: in attesa` };
    const goals = match.homeScore! + match.awayScore!;
    const reached = goals >= overMinGoals;
    const tone: ChipTone = reached ? 'winning' : finished ? 'losing' : 'pending';
    return {
      tone,
      visible: `${player.displayName} 🔥 ${goals}/${overMinGoals}`,
      sr: `${player.displayName} OVER: ${goals} gol su ${overMinGoals} necessari${reached ? ', soglia raggiunta' : finished ? ', mancato' : ', partita in corso'}`,
    };
  };

  const statusBadge = (m: MatchInfo) => {
    if (m.status === 'LIVE')
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-bold text-danger dark:bg-ink-800">
          <span aria-hidden="true" className="inline-block h-2 w-2 animate-pulse rounded-full bg-danger" />
          LIVE
        </span>
      );
    if (m.status === 'FINISHED')
      return (
        <span className="rounded-full bg-ink-100 px-2.5 py-0.5 text-xs font-bold text-ink-700 dark:bg-ink-800 dark:text-ink-200">
          Finita
        </span>
      );
    return (
      <span className="rounded-full bg-ink-100 px-2.5 py-0.5 text-xs font-medium text-ink-600 dark:bg-ink-800 dark:text-ink-300">
        {isTimeTBD(m.kickoffAt)
          ? 'orario da definire'
          : new Intl.DateTimeFormat('it-IT', { hour: '2-digit', minute: '2-digit' }).format(new Date(m.kickoffAt))}
      </span>
    );
  };

  return (
    <section aria-labelledby="sez-diretta" className="mt-8">
      <h2 id="sez-diretta" className="text-2xl font-bold tracking-tight">
        La diretta
      </h2>
      <p className="mt-1 text-ink-700 dark:text-ink-200">
        Partita per partita, come stanno andando i pronostici di tutti. Si aggiorna da sola.
      </p>

      <ul className="mt-4 flex flex-col gap-4">
        {matches.map((m) => {
          const chips = players
            .flatMap((p) => [chipFor(m, 'pick', p), chipFor(m, 'exact', p), chipFor(m, 'over', p)])
            .filter((c): c is NonNullable<typeof c> => c !== null);
          return (
            <li key={m.id}>
              <article
                aria-label={`${m.homeTeam.name} contro ${m.awayTeam.name}${
                  m.homeScore != null ? `, risultato ${m.homeScore} a ${m.awayScore}` : ''
                }`}
                className="rounded-2xl border border-ink-200 p-4 dark:border-ink-800"
              >
                <header className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="flex items-center gap-1.5 text-lg font-bold">
                    <Crest url={m.homeTeam.crestUrl} size={24} />
                    {m.homeTeam.shortName} <span aria-hidden="true">–</span>
                    <span className="visually-hidden">contro</span>
                    <Crest url={m.awayTeam.crestUrl} size={24} />
                    {m.awayTeam.shortName}
                  </h3>
                  <span className="flex items-center gap-3">
                    <span className="text-2xl font-bold tabular-nums">
                      {m.homeScore != null && m.awayScore != null
                        ? `${m.homeScore}-${m.awayScore}`
                        : '–'}
                    </span>
                    {statusBadge(m)}
                  </span>
                </header>
                {chips.length > 0 && (
                  <ul className="mt-3 flex flex-wrap gap-2">
                    {chips.map((chip, i) => (
                      <li key={i}>
                        <span
                          className={`inline-flex min-h-8 items-center gap-1 rounded-full border-2 px-2.5 py-0.5 text-sm font-semibold ${CHIP_CLASSES[chip.tone]}`}
                        >
                          <span aria-hidden="true">
                            {chip.tone === 'winning' ? '✓ ' : chip.tone === 'losing' ? '✗ ' : ''}
                            {chip.visible}
                          </span>
                          <span className="visually-hidden">{chip.sr}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </article>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * Classifica live: punteggi provvisori mentre le partite si giocano,
 * aggiornati man mano che i risultati arrivano (import ogni ~10 minuti).
 */
function LiveStandingsSection({ leagueId, matchdayId }: { leagueId: string; matchdayId: string }) {
  const live = useLiveStandings(leagueId, matchdayId, true);
  if (!live.data) return null;
  const { rows, matchesWithResult, totalMatches } = live.data;

  return (
    <section aria-labelledby="sez-live" className="mt-10">
      <h2 id="sez-live" className="text-2xl font-bold tracking-tight">
        Classifica live
        <span className="ml-3 inline-flex items-center gap-1.5 rounded-full bg-panda-100 px-3 py-1 align-middle text-sm font-semibold text-panda-900 dark:bg-panda-900 dark:text-panda-100">
          <span aria-hidden="true" className="inline-block h-2 w-2 animate-pulse rounded-full bg-panda-600 dark:bg-panda-400" />
          provvisoria
        </span>
      </h2>
      <p className="mt-1 text-ink-700 dark:text-ink-200" role="status">
        Risultati arrivati: {matchesWithResult} partite su {totalMatches}. I punteggi si aggiornano
        da soli man mano che le partite finiscono; quelli ufficiali arrivano alla pubblicazione.
      </p>

      {matchesWithResult === 0 ? (
        <p className="mt-4 text-lg">Nessuna partita è ancora iniziata: si aspetta il fischio d’inizio. ⚽</p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <caption className="visually-hidden">
              Classifica provvisoria: posizione, giocatore, punti di giornata e totale
            </caption>
            <thead>
              <tr className="border-b-2 border-ink-300 dark:border-ink-600">
                <th scope="col" className="py-2 pr-3">Posizione</th>
                <th scope="col" className="py-2 pr-3">Giocatore</th>
                <th scope="col" className="py-2 pr-3 text-right">Giornata</th>
                <th scope="col" className="py-2 text-right">Totale</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.memberId} className="border-b border-ink-200 dark:border-ink-800">
                  <td className="py-2.5 pr-3 text-lg font-bold">{row.position}</td>
                  <th scope="row" className="py-2.5 pr-3 font-medium">
                    {row.displayName}
                    {row.isBot && (
                      <span className="ml-2 rounded-full bg-panda-100 px-2 py-0.5 text-xs font-semibold text-panda-900 dark:bg-panda-900 dark:text-panda-100">
                        Bot
                      </span>
                    )}
                  </th>
                  <td className="py-2.5 pr-3 text-right font-semibold">
                    {row.provisionalPoints > 0 ? '+' : ''}
                    {formatPoints(row.provisionalPoints)}
                  </td>
                  <td className="py-2.5 text-right text-lg font-bold">
                    {formatPoints(row.cumulativePoints)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const PICK_SHORT: Record<string, string> = {
  HOME: '1',
  DRAW: 'X',
  AWAY: '2',
  HOME_DRAW: '1X',
  DRAW_AWAY: 'X2',
  HOME_AWAY: '12',
};

/**
 * Il tabellone delle schedine: partite in riga, giocatori in colonna,
 * segni nelle celle — come la lavagna al bar. Visibile su ogni giornata
 * dalla chiusura dei pronostici in poi (anche quelle passate).
 */
function RevealedPredictionsSection({
  leagueId,
  matchdayId,
  matches,
}: {
  leagueId: string;
  matchdayId: string;
  matches: MatchInfo[];
}) {
  const predictions = useMatchdayPredictions(leagueId, matchdayId, true);

  if (!predictions.data || predictions.data.length === 0) return null;
  const players = predictions.data;

  return (
    <section aria-labelledby="sez-schedine" className="mt-10">
      <h2 id="sez-schedine" className="text-2xl font-bold tracking-tight">
        Il tabellone delle schedine
      </h2>
      <p className="mt-1 text-ink-700 dark:text-ink-200">
        A pronostici chiusi, carte in tavola: ogni colonna è un giocatore.{' '}
        <span aria-hidden="true">🎯</span> risultato esatto, <span aria-hidden="true">🔥</span>{' '}
        OVER.
      </p>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <caption className="visually-hidden">
            Pronostici di tutti i giocatori: una riga per partita, una colonna per giocatore, con
            segno giocato, risultato esatto e OVER
          </caption>
          <thead>
            <tr className="border-b-2 border-ink-300 dark:border-ink-600">
              <th scope="col" className="py-2 pr-3">Partita</th>
              {players.map((p) => (
                <th key={p.memberId} scope="col" className="px-2 py-2 text-center align-bottom">
                  <span className="block whitespace-nowrap">{p.displayName}</span>
                  {p.isBot && (
                    <span className="mt-0.5 inline-block rounded-full bg-panda-100 px-2 py-0.5 text-xs font-semibold text-panda-900 dark:bg-panda-900 dark:text-panda-100">
                      Bot
                    </span>
                  )}
                  {p.isRedColumn && (
                    <span className="mt-0.5 inline-block rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-danger dark:bg-ink-800">
                      Colonna rossa
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matches.map((match) => (
              <tr key={match.id} className="border-b border-ink-200 dark:border-ink-800">
                <th scope="row" className="py-2.5 pr-3 font-medium whitespace-nowrap">
                  <Crest url={match.homeTeam.crestUrl} size={18} className="mr-1" />
                  {match.homeTeam.shortName} – <Crest url={match.awayTeam.crestUrl} size={18} className="mx-1" />
                  {match.awayTeam.shortName}
                  {match.homeScore != null && match.awayScore != null && (
                    <span className="ml-2 font-bold">
                      {match.homeScore}-{match.awayScore}
                    </span>
                  )}
                </th>
                {players.map((p) => {
                  const pick = p.items.find((i) => i.matchId === match.id)?.pick;
                  const isExact = p.exact?.matchId === match.id;
                  const isOver = p.overMatchId === match.id;
                  return (
                    <td key={p.memberId} className="px-2 py-2.5 text-center">
                      {pick && (
                        <strong className="text-base">
                          {PICK_SHORT[pick] ?? pick}
                          <span className="visually-hidden"> giocato</span>
                        </strong>
                      )}
                      {isExact && p.exact && (
                        <strong className="text-base">
                          <span aria-hidden="true">🎯 </span>
                          <span className="visually-hidden">risultato esatto </span>
                          {p.exact.home}-{p.exact.away}
                        </strong>
                      )}
                      {isOver && (
                        <span className={pick || isExact ? 'ml-1' : ''}>
                          <span aria-hidden="true">🔥</span>
                          <span className="visually-hidden">OVER su questa partita</span>
                        </span>
                      )}
                      {!pick && !isExact && !isOver && (
                        <span aria-hidden="true" className="text-ink-300 dark:text-ink-600">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** Form di contestazione (lineeguida §39). */
function DisputeSection({ leagueId, matchdayId }: { leagueId: string; matchdayId: string }) {
  const { announce } = useAnnouncer();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [saving, setSaving] = useState(false);

  if (sent) {
    return (
      <section aria-labelledby="sez-contesta" className="mt-10">
        <h2 id="sez-contesta" className="text-2xl font-bold tracking-tight">Contestazione</h2>
        <p role="status" className="mt-3 font-medium text-success">
          Contestazione inviata: l’amministratore della lega la esaminerà e riceverai una notifica.
        </p>
      </section>
    );
  }

  return (
    <section aria-labelledby="sez-contesta" className="mt-10">
      <h2 id="sez-contesta" className="text-2xl font-bold tracking-tight">
        Qualcosa non torna?
      </h2>
      {!open ? (
        <div className="mt-3">
          <Button variant="secondary" onClick={() => setOpen(true)}>
            Contesta un punteggio
          </Button>
        </div>
      ) : (
        <form
          noValidate
          className="mt-4 flex max-w-lg flex-col gap-4"
          onSubmit={async (e) => {
            e.preventDefault();
            setError(null);
            if (reason.trim().length < 3) {
              setError('Indica il motivo della contestazione.');
              announce('Errore: indica il motivo della contestazione.', 'assertive');
              return;
            }
            if (description.trim().length < 10) {
              setError('Descrivi il problema (almeno 10 caratteri).');
              announce('Errore: descrivi il problema.', 'assertive');
              return;
            }
            setSaving(true);
            try {
              await api(`/leagues/${leagueId}/disputes`, {
                method: 'POST',
                body: { leagueMatchdayId: matchdayId, reason: reason.trim(), description: description.trim() },
              });
              setSent(true);
              announce('Contestazione inviata. L’amministratore la esaminerà.');
            } catch (err) {
              const msg = err instanceof ApiError ? err.message : 'Invio non riuscito.';
              setError(msg);
              announce(`Errore: ${msg}`, 'assertive');
            } finally {
              setSaving(false);
            }
          }}
        >
          {error && (
            <p role="alert" className="font-medium text-danger">{error}</p>
          )}
          <div className="flex flex-col gap-1">
            <label htmlFor="disp-reason" className="font-medium">
              Motivo <span className="text-danger">*</span>
            </label>
            <input
              id="disp-reason"
              className="rounded-lg border border-ink-300 px-3 py-2.5 dark:border-ink-600 dark:bg-ink-900"
              placeholder="Es. OVER non conteggiato"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={120}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="disp-desc" className="font-medium">
              Descrizione <span className="text-danger">*</span>
            </label>
            <textarea
              id="disp-desc"
              rows={4}
              className="rounded-lg border border-ink-300 px-3 py-2.5 dark:border-ink-600 dark:bg-ink-900"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={2000}
            />
          </div>
          <div className="flex gap-3">
            <Button type="submit" loading={saving} loadingText="Invio…">
              Invia contestazione
            </Button>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Annulla
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}

/**
 * Scoreboard delle partite: card con squadre impilate e punteggio grande
 * (il dato per cui si apre la pagina), raggruppate per data così "orario
 * da definire" compare UNA volta e non dieci. La vincitrice è indicata
 * con peso tipografico + testo per screen reader, mai solo col colore.
 */
function MatchTable({
  matches,
  leagueId,
  matchdayId,
  canEditResults,
}: {
  matches: MatchInfo[];
  leagueId: string;
  matchdayId: string;
  canEditResults: boolean;
}) {
  const { announce } = useAnnouncer();
  const setResult = useSetResult(leagueId, matchdayId);
  const [editing, setEditing] = useState<Record<string, { home: string; away: string }>>({});

  // Raggruppa per giorno (fuso di Roma), nell'ordine originale.
  const groups: Array<{ label: string; allTBD: boolean; items: MatchInfo[] }> = [];
  for (const m of matches) {
    const label = formatDateOnly(m.kickoffAt);
    const last = groups[groups.length - 1];
    if (last && last.label === label) {
      last.items.push(m);
      last.allTBD = last.allTBD && isTimeTBD(m.kickoffAt);
    } else {
      groups.push({ label, allTBD: isTimeTBD(m.kickoffAt), items: [m] });
    }
  }

  const timeChip = (m: MatchInfo): { text: string; live?: boolean } => {
    if (m.status === 'LIVE') return { text: 'LIVE', live: true };
    if (m.homeScore != null && m.awayScore != null && m.status === 'FINISHED')
      return { text: 'Finale' };
    if (isTimeTBD(m.kickoffAt)) return { text: 'ora da definire' };
    return {
      text: new Intl.DateTimeFormat('it-IT', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/Rome',
      }).format(new Date(m.kickoffAt)),
    };
  };

  const srSentence = (m: MatchInfo): string => {
    const finished = m.homeScore != null && m.awayScore != null;
    if (!finished) {
      return `${m.homeTeam.name} contro ${m.awayTeam.name}, ${
        m.status === 'LIVE' ? 'in corso' : `calcio d'inizio ${formatKickoff(m.kickoffAt)}`
      }.`;
    }
    const esito =
      m.homeScore! > m.awayScore!
        ? `vince ${m.homeTeam.name}`
        : m.homeScore! < m.awayScore!
          ? `vince ${m.awayTeam.name}`
          : 'pareggio';
    return `${m.homeTeam.name} ${m.homeScore}, ${m.awayTeam.name} ${m.awayScore} — ${
      m.status === 'LIVE' ? 'in corso' : 'finale'
    }, ${esito}.`;
  };

  return (
    <div className="mt-4 flex flex-col gap-5">
      {groups.map((g) => (
        <section key={g.label} aria-label={`Partite di ${g.label}`}>
          <h3 className="flex flex-wrap items-baseline gap-x-2 text-base font-semibold text-ink-700 dark:text-ink-200">
            <span className="first-letter:uppercase">{g.label}</span>
            {g.allTBD && (
              <span className="text-sm font-normal text-ink-500 dark:text-ink-400">
                orari ufficiali non ancora pubblicati
              </span>
            )}
          </h3>
          <ul className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {g.items.map((m) => {
              const finished = m.homeScore != null && m.awayScore != null;
              const homeWin = finished && m.homeScore! > m.awayScore!;
              const awayWin = finished && m.awayScore! > m.homeScore!;
              const chip = timeChip(m);
              const edit = editing[m.id] ?? {
                home: m.homeScore != null ? String(m.homeScore) : '',
                away: m.awayScore != null ? String(m.awayScore) : '',
              };
              const teamRow = (
                team: MatchInfo['homeTeam'],
                score: number | null,
                isWinner: boolean,
                isLoser: boolean,
              ) => (
                <div className="flex min-w-0 items-center gap-2.5">
                  <Crest url={team.crestUrl} size={24} className="shrink-0" />
                  <span
                    className={`min-w-0 flex-1 truncate ${
                      isWinner ? 'font-bold' : isLoser ? 'text-ink-500 dark:text-ink-400' : 'font-medium'
                    }`}
                  >
                    {team.name}
                  </span>
                  <span
                    className={`text-2xl font-bold leading-none tabular-nums ${
                      isLoser ? 'text-ink-400 dark:text-ink-500' : ''
                    }`}
                  >
                    {score ?? '–'}
                  </span>
                </div>
              );
              return (
                <li
                  key={m.id}
                  className="rounded-2xl border border-ink-200 p-4 dark:border-ink-800"
                >
                  <p className="visually-hidden">{srSentence(m)}</p>
                  <div aria-hidden="true">
                    <div className="mb-2 flex items-center justify-end">
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-bold ${
                          chip.live
                            ? 'bg-red-50 text-danger dark:bg-ink-800'
                            : 'bg-ink-100 text-ink-600 dark:bg-ink-800 dark:text-ink-300'
                        }`}
                      >
                        {chip.live && (
                          <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-danger" />
                        )}
                        {chip.text}
                      </span>
                    </div>
                    <div className="flex flex-col gap-2">
                      {teamRow(m.homeTeam, m.homeScore, homeWin, awayWin)}
                      {teamRow(m.awayTeam, m.awayScore, awayWin, homeWin)}
                    </div>
                  </div>
                  {canEditResults && (
                    <div className="mt-3 flex items-center gap-2 border-t border-ink-200 pt-3 dark:border-ink-800">
                      <label className="visually-hidden" htmlFor={`res-h-${m.id}`}>
                        Gol {m.homeTeam.name}
                      </label>
                      <input
                        id={`res-h-${m.id}`}
                        type="number"
                        min={0}
                        max={30}
                        inputMode="numeric"
                        className="w-14 rounded-lg border border-ink-300 px-2 py-1.5 text-center tabular-nums dark:border-ink-600 dark:bg-ink-900"
                        value={edit.home}
                        onChange={(e) => setEditing({ ...editing, [m.id]: { ...edit, home: e.target.value } })}
                      />
                      <span aria-hidden="true">–</span>
                      <label className="visually-hidden" htmlFor={`res-a-${m.id}`}>
                        Gol {m.awayTeam.name}
                      </label>
                      <input
                        id={`res-a-${m.id}`}
                        type="number"
                        min={0}
                        max={30}
                        inputMode="numeric"
                        className="w-14 rounded-lg border border-ink-300 px-2 py-1.5 text-center tabular-nums dark:border-ink-600 dark:bg-ink-900"
                        value={edit.away}
                        onChange={(e) => setEditing({ ...editing, [m.id]: { ...edit, away: e.target.value } })}
                      />
                      <Button
                        variant="secondary"
                        onClick={async () => {
                          if (edit.home === '' || edit.away === '') {
                            announce('Inserisci entrambi i punteggi.', 'assertive');
                            return;
                          }
                          await setResult.mutateAsync({
                            matchId: m.id,
                            homeScore: Number(edit.home),
                            awayScore: Number(edit.away),
                          });
                          announce(
                            `Risultato salvato: ${m.homeTeam.shortName} ${edit.home}, ${m.awayTeam.shortName} ${edit.away}.`,
                          );
                        }}
                      >
                        Salva
                        <span className="visually-hidden">
                          {' '}risultato di {m.homeTeam.name} contro {m.awayTeam.name}
                        </span>
                      </Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}

function ScoresSection({
  leagueId,
  matchdayId,
  isAdmin,
}: {
  leagueId: string;
  matchdayId: string;
  isAdmin: boolean;
}) {
  const scores = useMatchdayScores(leagueId, matchdayId);
  const { announce } = useAnnouncer();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [correcting, setCorrecting] = useState<string | null>(null);
  const [newTotal, setNewTotal] = useState('');
  const [correctionReason, setCorrectionReason] = useState('');
  const [correctionError, setCorrectionError] = useState<string | null>(null);
  const [savingCorrection, setSavingCorrection] = useState(false);

  if (!scores.data) return null;

  const submitCorrection = async (memberId: string, displayName: string) => {
    setCorrectionError(null);
    if (newTotal === '' || Number.isNaN(Number(newTotal))) {
      setCorrectionError('Inserisci il nuovo totale.');
      return;
    }
    if (correctionReason.trim().length < 5) {
      setCorrectionError('Il motivo della correzione è obbligatorio (almeno 5 caratteri).');
      return;
    }
    setSavingCorrection(true);
    try {
      await api(`/leagues/${leagueId}/matchdays/${matchdayId}/scores/${memberId}`, {
        method: 'PATCH',
        body: { total: Number(newTotal), reason: correctionReason.trim() },
      });
      announce(`Punteggio di ${displayName} corretto a ${newTotal}. Classifica aggiornata.`);
      setCorrecting(null);
      setNewTotal('');
      setCorrectionReason('');
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'matchdays', matchdayId, 'scores'] });
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'standings'] });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Correzione non riuscita.';
      setCorrectionError(msg);
      announce(`Errore: ${msg}`, 'assertive');
    } finally {
      setSavingCorrection(false);
    }
  };

  return (
    <section aria-labelledby="sez-punteggi" className="mt-10">
      <h2 id="sez-punteggi" className="text-2xl font-bold tracking-tight">Punteggi della giornata</h2>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <caption className="visually-hidden">
            Punteggi della giornata per ogni partecipante, ordinati dal più alto
          </caption>
          <thead>
            <tr className="border-b-2 border-ink-300 dark:border-ink-600">
              <th scope="col" className="py-2 pr-3">Giocatore</th>
              <th scope="col" className="py-2 pr-3 text-right">Totale</th>
              <th scope="col" className="py-2">Dettaglio</th>
            </tr>
          </thead>
          <tbody>
            {scores.data.scores.map((s) => (
              <Fragment key={s.memberId}>
                <tr className="border-b border-ink-200 dark:border-ink-800">
                  <th scope="row" className="py-3 pr-3 font-medium">
                    {s.displayName}
                    {s.isBot && (
                      <span className="ml-2 rounded-full bg-panda-100 px-2 py-0.5 text-xs font-semibold text-panda-900 dark:bg-panda-900 dark:text-panda-100">
                        Bot
                      </span>
                    )}
                  </th>
                  <td className="py-3 pr-3 text-right text-lg font-bold">{formatPoints(s.total)}</td>
                  <td className="py-3">
                    <span className="flex flex-wrap items-center gap-2">
                      {s.breakdown ? (
                        <button
                          type="button"
                          className="min-h-11 rounded-lg px-3 py-2 font-medium text-panda-700 underline dark:text-panda-300"
                          aria-expanded={expanded === s.memberId}
                          onClick={() => setExpanded(expanded === s.memberId ? null : s.memberId)}
                        >
                          {expanded === s.memberId ? 'Nascondi dettaglio' : 'Perché questo punteggio?'}
                          <span className="visually-hidden"> di {s.displayName}</span>
                        </button>
                      ) : (
                        <span className="text-sm text-ink-500 dark:text-ink-400">—</span>
                      )}
                      {isAdmin && (
                        <button
                          type="button"
                          className="min-h-11 rounded-lg px-3 py-2 font-medium text-panda-700 underline dark:text-panda-300"
                          aria-expanded={correcting === s.memberId}
                          onClick={() => {
                            setCorrecting(correcting === s.memberId ? null : s.memberId);
                            setNewTotal(String(s.total));
                            setCorrectionReason('');
                            setCorrectionError(null);
                          }}
                        >
                          Correggi
                          <span className="visually-hidden"> il punteggio di {s.displayName}</span>
                        </button>
                      )}
                      {s.manuallyAdjusted && (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-warning dark:bg-ink-800">
                          Corretto a mano
                        </span>
                      )}
                    </span>
                  </td>
                </tr>
                {correcting === s.memberId && (
                  <tr className="border-b border-ink-200 dark:border-ink-800">
                    <td colSpan={3} className="bg-ink-50 px-4 py-3 dark:bg-ink-900">
                      <form
                        noValidate
                        className="flex flex-wrap items-end gap-3"
                        onSubmit={(e) => {
                          e.preventDefault();
                          submitCorrection(s.memberId, s.displayName);
                        }}
                      >
                        {correctionError && (
                          <p role="alert" className="w-full font-medium text-danger">
                            {correctionError}
                          </p>
                        )}
                        <div className="flex flex-col gap-1">
                          <label htmlFor={`corr-total-${s.memberId}`} className="font-medium">
                            Nuovo totale di {s.displayName}
                          </label>
                          <input
                            id={`corr-total-${s.memberId}`}
                            type="number"
                            step={0.25}
                            className="w-28 rounded-lg border border-ink-300 px-3 py-2 dark:border-ink-600 dark:bg-ink-950"
                            value={newTotal}
                            onChange={(e) => setNewTotal(e.target.value)}
                          />
                        </div>
                        <div className="flex flex-1 flex-col gap-1">
                          <label htmlFor={`corr-reason-${s.memberId}`} className="font-medium">
                            Motivo <span className="text-danger">*</span>
                          </label>
                          <input
                            id={`corr-reason-${s.memberId}`}
                            className="rounded-lg border border-ink-300 px-3 py-2 dark:border-ink-600 dark:bg-ink-950"
                            placeholder="Finirà nell'audit log e nel dettaglio del punteggio"
                            value={correctionReason}
                            onChange={(e) => setCorrectionReason(e.target.value)}
                          />
                        </div>
                        <Button type="submit" loading={savingCorrection} loadingText="Correzione…">
                          Salva correzione
                        </Button>
                      </form>
                    </td>
                  </tr>
                )}
                {expanded === s.memberId && s.breakdown && (
                  <tr className="border-b border-ink-200 dark:border-ink-800">
                    <td colSpan={3} className="bg-ink-50 px-4 py-3 dark:bg-ink-900">
                      <h3 className="font-semibold">Dettaglio del calcolo</h3>
                      <ul className="mt-2 flex flex-col gap-1">
                        {s.breakdown.map((entry, i) => (
                          <li key={i} className="flex justify-between gap-3">
                            <span>{entry.label}</span>
                          </li>
                        ))}
                        <li className="mt-1 flex justify-between gap-3 border-t border-ink-300 pt-2 font-bold dark:border-ink-600">
                          <span>Totale</span>
                          <span>{formatPoints(s.total)}</span>
                        </li>
                      </ul>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function AdminPanel({ leagueId, matchdayId, state }: { leagueId: string; matchdayId: string; state: string }) {
  const { announce } = useAnnouncer();
  const transition = useTransitionMatchday(leagueId, matchdayId);
  const status = useMatchdayStatus(leagueId, matchdayId, state === 'OPEN' || state === 'SCHEDULED');
  const [error, setError] = useState<string | null>(null);

  const actions = NEXT_TRANSITIONS[state] ?? [];

  return (
    <section aria-labelledby="sez-admin" className="mt-10 rounded-2xl border border-ink-200 p-5 dark:border-ink-800">
      <h2 id="sez-admin" className="text-xl font-bold tracking-tight">Gestione giornata (amministrazione)</h2>

      {status.data && (
        <p role="status" className="mt-3">
          {status.data.submitted} su {status.data.total} partecipanti hanno inviato i pronostici.
          {status.data.submitted < status.data.total &&
            ` Mancano: ${status.data.members
              .filter((m) => !m.hasSubmitted)
              .map((m) => m.displayName)
              .join(', ')}.`}
        </p>
      )}

      {error && (
        <p role="alert" className="mt-3 font-medium text-danger">{error}</p>
      )}

      <div className="mt-4 flex flex-wrap gap-3">
        {actions.map((a) => (
          <Button
            key={a.to}
            variant={a.secondary ? 'secondary' : 'primary'}
            onClick={async () => {
              setError(null);
              if (a.confirm && !window.confirm(a.confirm)) return;
              try {
                await transition.mutateAsync(a.to);
                announce(`Fatto: ${a.label.toLowerCase()}.`);
              } catch (err) {
                const message = err instanceof ApiError ? err.message : 'Operazione non riuscita.';
                setError(message);
                announce(`Errore: ${message}`, 'assertive');
              }
            }}
            loading={transition.isPending}
          >
            {a.label}
          </Button>
        ))}
      </div>
    </section>
  );
}
